"use client";

import { medimoQC } from "@medimo/core-api/internal/next";
import type { components } from "@medimo/core-api/internal/openapi";
import {
  Checkbox,
  Label,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@medimo/ui";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format, toZonedTime } from "date-fns-tz";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";

type Notification = components["schemas"]["UserNotificationReadModel"];

const UNREAD_ONLY_FILTER_ID = "notifications-unread-only";
const INCLUDE_PAST_TOGGLE_ID = "notifications-include-past";

const columns: ColumnDef<Notification>[] = [
  {
    id: "status",
    header: "",
    cell: ({ row }) =>
      row.original.is_read ? null : (
        <span
          role="img"
          aria-label="未読"
          className="mx-auto block h-2 w-2 rounded-full bg-red-500"
        />
      ),
  },
  {
    accessorKey: "title",
    header: "タイトル",
    cell: ({ row }) => (
      <Link
        href={`/notifications/${row.original.id}`}
        className="block h-full w-full p-4"
      >
        {row.original.title}
      </Link>
    ),
  },
  {
    accessorKey: "publish_at",
    header: "公開日時",
    cell: ({ row }) => (
      <Link
        href={`/notifications/${row.original.id}`}
        className="block h-full w-full p-4"
      >
        {format(
          toZonedTime(new Date(row.original.publish_at * 1000), "Asia/Tokyo"),
          "yyyy/MM/dd HH:mm",
        )}
      </Link>
    ),
  },
];

export const NotificationsTable = () => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [includePast, setIncludePast] = useState(false);

  const { data, isLoading } = medimoQC.useQuery("get", "/v2/notifications", {
    params: { query: { include_past: includePast } },
  });
  const notifications = data?.notifications;

  const visibleNotifications = useMemo(() => {
    if (!notifications) return notifications;
    return unreadOnly
      ? notifications.filter((notification) => !notification.is_read)
      : notifications;
  }, [notifications, unreadOnly]);

  const empty = useMemo(() => [], []);
  const table = useReactTable({
    data: visibleNotifications ?? empty,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const emptyMessage = unreadOnly
    ? "未読のお知らせはありません"
    : "お知らせはありません";

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Checkbox
            id={UNREAD_ONLY_FILTER_ID}
            checked={unreadOnly}
            onCheckedChange={(checked) => setUnreadOnly(checked === true)}
          />
          <Label htmlFor={UNREAD_ONLY_FILTER_ID}>未読のみ表示</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={INCLUDE_PAST_TOGGLE_ID}
            checked={includePast}
            onCheckedChange={setIncludePast}
          />
          <Label htmlFor={INCLUDE_PAST_TOGGLE_ID}>過去のお知らせも見る</Label>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                {isLoading ? (
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <p>ローディング中</p>
                    </div>
                  </TableCell>
                ) : (
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {emptyMessage}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
